import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, TextField, CircularProgress } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import clientService from 'src/clientService';
import { ConfirmationStatus, SpeciesConfirmationResponse } from 'src/api/Api';

const GREEN = '#10b981';
const RED = '#ef4444';

const extractErrorMessage = (err: unknown, fallback: string): string => {
  if (typeof err === 'object' && err !== null) {
    const errorObj = err as { message?: string; error?: string | { message?: string } };
    if (typeof errorObj.error === 'string') return errorObj.error;
    if (errorObj.error && typeof errorObj.error === 'object' && errorObj.error.message) return errorObj.error.message;
    if (errorObj.message) return errorObj.message;
  }
  return fallback;
};

interface SpeciesConfirmationCardProps {
  audioFile: string;
  detectedSpecies: string;
  deploymentId?: string;
  fileId?: string;
}

/**
 * Lets a reviewer approve a detected species, or reject it with the correct species.
 * The verdict is stored per (deployment, audio file, detected species); remount the
 * card (via `key`) when any of those change.
 */
const SpeciesConfirmationCard: React.FC<SpeciesConfirmationCardProps> = ({
  audioFile,
  detectedSpecies,
  deploymentId,
  fileId,
}) => {
  const [saved, setSaved] = useState<SpeciesConfirmationResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submitting, setSubmitting] = useState<'approve' | 'reject' | null>(null);
  const [isEditingRejection, setIsEditingRejection] = useState(false);
  const [manualSpecies, setManualSpecies] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!deploymentId) return;

    let cancelled = false;
    const fetchSaved = async () => {
      setIsLoading(true);
      try {
        const resp = await clientService.api.listSpeciesConfirmationsApiV1SpeciesConfirmationsGet({
          deployment_id: deploymentId,
          audio_file: audioFile,
          detected_species: detectedSpecies,
          limit: 1,
        });
        if (!cancelled) setSaved(resp.data?.data?.[0] ?? null);
      } catch (err: unknown) {
        if (!cancelled) setError(extractErrorMessage(err, 'Could not load the saved confirmation.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchSaved();
    return () => {
      cancelled = true;
    };
  }, [audioFile, detectedSpecies, deploymentId]);

  const canSubmit = Boolean(deploymentId) && !isLoading && submitting === null;
  const isApproved = saved?.status === ConfirmationStatus.Approved;
  const isRejected = saved?.status === ConfirmationStatus.Rejected;

  const target = deploymentId
    ? { detected_species: detectedSpecies, audio_file: audioFile, deployment_id: deploymentId, file_id: fileId }
    : null;

  const submit = async (
    action: 'approve' | 'reject',
    request: () => Promise<{ data: SpeciesConfirmationResponse }>,
  ) => {
    setSubmitting(action);
    setError(null);
    try {
      const resp = await request();
      setSaved(resp.data);
      setIsEditingRejection(false);
    } catch (err: unknown) {
      console.error(`Failed to ${action} species:`, err);
      setError(extractErrorMessage(
        err,
        action === 'approve'
          ? 'Failed to approve species. Please try again.'
          : 'Failed to submit rejection. Please try again.',
      ));
    } finally {
      setSubmitting(null);
    }
  };

  const handleApprove = () => {
    if (!canSubmit || !target || isApproved) return;
    submit('approve', () => clientService.api.approveSpeciesApiV1SpeciesConfirmationsApprovePost(target));
  };

  const handleRejectClick = () => {
    setError(null);
    setManualSpecies(saved?.suggested_species ?? '');
    setIsEditingRejection(true);
  };

  const handleRejectSubmit = () => {
    const suggested = manualSpecies.trim();
    if (!canSubmit || !target || !suggested) return;
    submit('reject', () => clientService.api.rejectSpeciesApiV1SpeciesConfirmationsRejectPost({
      ...target,
      suggested_species: suggested,
    }));
  };

  return (
    <Box sx={{ mt: 1, p: 1.5, border: '1.5px solid rgba(255, 255, 255, 0.05)', borderRadius: '10px', bgcolor: 'rgba(255, 255, 255, 0.01)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontSize: '13px', color: '#fff', fontWeight: 600 }}>
          Species Confirmation
        </Typography>
        {isLoading && <CircularProgress size={12} sx={{ color: GREEN }} aria-label="Loading saved confirmation" />}
      </Box>
      <Typography sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', mb: 1.5 }}>
        Do you approve the detected species ({detectedSpecies})?
      </Typography>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          size="small"
          variant={isApproved ? 'contained' : 'outlined'}
          disabled={!canSubmit}
          onClick={handleApprove}
          sx={{
            flex: 1,
            fontSize: '10px',
            borderRadius: '6px',
            py: 0.5,
            textTransform: 'none',
            bgcolor: isApproved ? GREEN : 'transparent',
            color: isApproved ? '#000' : GREEN,
            border: isApproved ? 'none' : '1px solid rgba(16, 185, 129, 0.5)',
            '&:hover': {
              bgcolor: isApproved ? GREEN : 'rgba(16, 185, 129, 0.1)',
              border: isApproved ? 'none' : `1px solid ${GREEN}`,
            },
            '&.Mui-disabled': {
              bgcolor: isApproved ? 'rgba(16, 185, 129, 0.5)' : 'transparent',
              color: 'rgba(255, 255, 255, 0.3)',
            },
          }}
        >
          {submitting === 'approve' && <CircularProgress size={10} sx={{ mr: 0.5, color: 'inherit' }} />}
          Approve
        </Button>
        <Button
          size="small"
          variant={isRejected || isEditingRejection ? 'contained' : 'outlined'}
          color="error"
          disabled={!canSubmit}
          onClick={handleRejectClick}
          sx={{
            flex: 1,
            fontSize: '10px',
            borderRadius: '6px',
            py: 0.5,
            textTransform: 'none',
          }}
        >
          Reject
        </Button>
      </Box>

      {isEditingRejection && (
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.6)', mb: 1 }}>
            Please enter the correct species:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              autoFocus
              placeholder="Manual species name..."
              value={manualSpecies}
              disabled={submitting !== null}
              onChange={(e) => setManualSpecies(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRejectSubmit();
                if (e.key === 'Escape') {
                  e.stopPropagation(); // keep the dialog open
                  setIsEditingRejection(false);
                }
              }}
              inputProps={{ maxLength: 255, 'aria-label': 'Correct species name' }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  fontSize: '11px',
                  bgcolor: 'rgba(0,0,0,0.2)',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                  '&:hover fieldset': { borderColor: GREEN },
                  '&.Mui-focused fieldset': { borderColor: GREEN },
                },
              }}
            />
            <Button
              size="small"
              variant="contained"
              color="error"
              disabled={!manualSpecies.trim() || !canSubmit}
              onClick={handleRejectSubmit}
              sx={{
                fontSize: '10px',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                px: 1.5,
                borderRadius: '6px',
              }}
            >
              {submitting === 'reject' && <CircularProgress size={10} sx={{ mr: 0.5, color: 'inherit' }} />}
              Save
            </Button>
          </Box>
        </Box>
      )}

      {!deploymentId && (
        <Typography sx={{ mt: 1.5, fontSize: '10.5px', color: 'rgba(255, 255, 255, 0.5)' }}>
          Select a deployment to confirm this species.
        </Typography>
      )}

      {saved && !isEditingRejection && (isApproved || isRejected) && (
        <Box
          role="status"
          sx={{
            mt: 1.5,
            p: 1,
            borderRadius: '6px',
            bgcolor: isApproved ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            border: '1px solid',
            borderColor: isApproved ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: 0.8,
          }}
        >
          {isApproved
            ? <CheckCircleIcon sx={{ color: GREEN, fontSize: 14 }} />
            : <CancelIcon sx={{ color: RED, fontSize: 14 }} />}
          <Typography sx={{ fontSize: '10.5px', color: isApproved ? GREEN : RED, fontWeight: 600 }}>
            {isApproved ? 'Approved' : `Rejected — suggested: ${saved.suggested_species}`}
          </Typography>
        </Box>
      )}

      {error && (
        <Box role="alert" sx={{ mt: 1.5, p: 1, borderRadius: '6px', bgcolor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
          <Typography sx={{ fontSize: '10.5px', color: RED }}>
            {error}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SpeciesConfirmationCard;
